/**
 * «Posar al dia» (`plans/fitxes/20261012-posar-al-dia.md`): tota la lògica de la taula, pura.
 *
 * De la proposta del backend a l'estat inicial de les caselles, la coherència entre «Entrat» i
 * «Marxat», la casella «totes» de cada columna, la llista d'accions que s'envia i els textos. El
 * component només pinta el que surt d'aquí.
 */
import { format } from 'date-fns';
import type {
  StayCatchUpApplyResponse,
  StayCatchUpItem,
  StayCatchUpResult,
  StayCatchUpRow,
  StayCatchUpUndoResponse,
} from '@guilleumes/types';
import { langToLocale, parseCalendarDate } from '@/lib/dates';

export type CatchUpColumn = 'in' | 'out' | 'clean';
export const CATCH_UP_COLUMNS: readonly CatchUpColumn[] = ['in', 'out', 'clean'];

export const COLUMN_LABEL: Record<CatchUpColumn, string> = {
  in: 'Entrat',
  out: 'Marxat',
  clean: 'Neta',
};

/** Les caselles marcades d'una fila (només les que encara es poden marcar). */
export interface RowChecks {
  in: boolean;
  out: boolean;
  clean: boolean;
}

/** `reservationId` → caselles. */
export type CatchUpChecks = Record<string, RowChecks>;

/** `done`: ja consta, es veu fet i no es toca. `na`: en aquesta fila no hi té sentit. */
export type CellState = 'done' | 'na' | 'open';

export function cellState(row: StayCatchUpRow, column: CatchUpColumn): CellState {
  if (column === 'in') return row.checkedIn ? 'done' : 'open';
  if (column === 'out') return row.checkedOut ? 'done' : 'open';
  if (row.cleanSinceCheckout) return 'done';
  return row.canClean && row.physicalRoomId ? 'open' : 'na';
}

const NO_CHECKS: RowChecks = { in: false, out: false, clean: false };

/** Coherència d'una fila: no es pot marcar «Marxat» sense «Entrat» si l'entrada encara no consta. */
function coherent(row: StayCatchUpRow, checks: RowChecks): RowChecks {
  const open = (c: CatchUpColumn) => cellState(row, c) === 'open';
  const out = open('out') && checks.out;
  return {
    in: open('in') && (checks.in || out),
    out,
    clean: open('clean') && checks.clean,
  };
}

/**
 * L'avís quan el backend no ha pogut llegir les obertures de porta (`doorsUnavailable`): qui ha
 * entrat amb el seu codi pot sortir amb «Entrat» buit.
 */
export const DOORS_UNAVAILABLE_TEXT =
  "No s'han pogut llegir les portes. No es proposa cap entrada; si en marques una, es desarà amb l'hora d'ara.";

/**
 * Estat inicial: «Entrat» i «Marxat» segons la proposta; «Neta», sempre sense marcar.
 *
 * Sense portes (`doorsUnavailable`) no es proposa res a qui no consta com a entrat: ni «Entrat» ni
 * «Marxat», que el marcaria també. Es pot marcar a mà igualment: el backend l'accepta i hi desa
 * l'hora d'aquell moment.
 */
export function initialChecks(rows: readonly StayCatchUpRow[], doorsUnavailable = false): CatchUpChecks {
  const checks: CatchUpChecks = {};
  for (const row of rows) {
    const propose = !(doorsUnavailable && !row.checkedIn);
    checks[row.reservationId] = coherent(row, {
      in: propose && row.suggestIn,
      out: propose && row.suggestOut,
      clean: false,
    });
  }
  return checks;
}

export const checksOf = (checks: CatchUpChecks, row: StayCatchUpRow): RowChecks =>
  checks[row.reservationId] ?? NO_CHECKS;

function setRow(row: StayCatchUpRow, current: RowChecks, column: CatchUpColumn, value: boolean): RowChecks {
  if (cellState(row, column) !== 'open') return current;
  const next = { ...current, [column]: value };
  // Desmarcar «Entrat» desmarca «Marxat» (marcar «Marxat» marca «Entrat»: ho fa `coherent`).
  if (column === 'in' && !value && cellState(row, 'in') === 'open') next.out = false;
  return coherent(row, next);
}

/** Marca o desmarca una casella. Les fetes i les que no hi tenen sentit no es toquen. */
export function toggleCell(
  checks: CatchUpChecks,
  row: StayCatchUpRow,
  column: CatchUpColumn,
  value: boolean,
): CatchUpChecks {
  return { ...checks, [row.reservationId]: setRow(row, checksOf(checks, row), column, value) };
}

/** La casella «totes» d'una columna: totes les que encara es poden marcar. */
export function toggleColumn(
  checks: CatchUpChecks,
  rows: readonly StayCatchUpRow[],
  column: CatchUpColumn,
  value: boolean,
): CatchUpChecks {
  const next = { ...checks };
  for (const row of rows) next[row.reservationId] = setRow(row, checksOf(checks, row), column, value);
  return next;
}

/** `empty`: a la columna no hi ha res per marcar (la casella «totes» es desactiva). */
export type ColumnState = 'empty' | 'none' | 'some' | 'all';

export function columnState(
  checks: CatchUpChecks,
  rows: readonly StayCatchUpRow[],
  column: CatchUpColumn,
): ColumnState {
  let open = 0;
  let checked = 0;
  for (const row of rows) {
    if (cellState(row, column) !== 'open') continue;
    open += 1;
    if (checksOf(checks, row)[column]) checked += 1;
  }
  if (open === 0) return 'empty';
  if (checked === 0) return 'none';
  return checked === open ? 'all' : 'some';
}

/** El que s'envia a `POST /stay-catch-up/apply`: entrades, sortides i netes (una per habitació). */
export function buildItems(checks: CatchUpChecks, rows: readonly StayCatchUpRow[]): StayCatchUpItem[] {
  const ins: StayCatchUpItem[] = [];
  const outs: StayCatchUpItem[] = [];
  const cleans: StayCatchUpItem[] = [];
  const cleanRooms = new Set<string>();
  for (const row of rows) {
    const c = coherent(row, checksOf(checks, row));
    if (c.in) ins.push({ action: 'CHECK_IN', reservationId: row.reservationId });
    if (c.out) outs.push({ action: 'CHECK_OUT', reservationId: row.reservationId });
    if (c.clean && row.physicalRoomId && !cleanRooms.has(row.physicalRoomId)) {
      cleanRooms.add(row.physicalRoomId);
      cleans.push({ action: 'CLEAN', physicalRoomId: row.physicalRoomId });
    }
  }
  return [...ins, ...outs, ...cleans];
}

/** «Marcar 14»; sense cap acció, «Marcar» (i el botó va desactivat). */
export const confirmLabel = (count: number): string => (count > 0 ? `Marcar ${count}` : 'Marcar');

/**
 * «12 oct»: dia i mes curt, en català. Sense dia de la setmana: el `EEEEEE` de date-fns escriu «dm» per
 * dimecres i la capçalera del calendari (Intl) hi escriu «dc»; dos codis diferents confondrien.
 */
export function formatShortDay(iso: string, lang = 'ca'): string {
  const date = parseCalendarDate(iso);
  if (!date) return '';
  return format(date, 'd MMM', { locale: langToLocale(lang) }).replace('.', '');
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** «12 fetes, 2 ja ho estaven» (i «, 1 rebutjada» si n'hi ha). */
export function applySummaryText(summary: StayCatchUpApplyResponse['summary']): string {
  const parts = [plural(summary.done, 'feta', 'fetes')];
  if (summary.already > 0) parts.push(`${summary.already} ja ho ${summary.already === 1 ? 'estava' : 'estaven'}`);
  if (summary.rejected > 0) parts.push(plural(summary.rejected, 'rebutjada', 'rebutjades'));
  return parts.join(', ');
}

/** «3 desfetes» (i «, 1 sense tocar» si algú ho havia canviat després). */
export function undoSummaryText(summary: StayCatchUpUndoResponse['summary']): string {
  const parts = [plural(summary.undone, 'desfeta', 'desfetes')];
  if (summary.skipped > 0) parts.push(`${summary.skipped} sense tocar (havien canviat)`);
  return parts.join(', ');
}

const ACTION_LABEL: Record<StayCatchUpResult['action'], string> = {
  CHECK_IN: COLUMN_LABEL.in,
  CHECK_OUT: COLUMN_LABEL.out,
  CLEAN: COLUMN_LABEL.clean,
};

export interface RejectedLine {
  key: string;
  /** «H102 · Marxat» */
  what: string;
  why: string;
}

/** Els elements rebutjats, amb l'habitació de la fila (la resposta no la porta). */
export function rejectedLines(
  results: readonly StayCatchUpResult[],
  rows: readonly StayCatchUpRow[],
): RejectedLine[] {
  const lines: RejectedLine[] = [];
  results.forEach((r, index) => {
    if (r.outcome !== 'REJECTED') return;
    const row = rows.find((x) =>
      r.action === 'CLEAN' ? x.physicalRoomId === r.physicalRoomId : x.reservationId === r.reservationId,
    );
    lines.push({
      key: `${index}`,
      what: `${row?.roomName ?? 'Habitació'} · ${ACTION_LABEL[r.action]}`,
      why: r.message ?? "No s'ha pogut desar",
    });
  });
  return lines;
}

/**
 * Les caselles tocades a mà (`null` = cap: es pinta la proposta) després d'una crida d'aplicar.
 *
 * Amb algun rebuig el diàleg es queda obert i la llista es torna a carregar: el que s'havia tocat es
 * conserva, perquè tornar a la proposta tornaria a marcar el que l'usuari havia desmarcat. El que ja
 * s'ha fet no es torna a enviar (`buildItems` només mira les caselles encara obertes). Sense cap
 * rebuig el diàleg es tanca i es torna a la proposta.
 */
export function editedAfterApply(
  edited: CatchUpChecks | null,
  summary: StayCatchUpApplyResponse['summary'],
): CatchUpChecks | null {
  return summary.rejected > 0 ? edited : null;
}
